import { appConfigService } from "./AppConfigService";
import { filesService } from "./FilesService";
import { textToJsonParserService } from "./TextToJsonParserService";
import { getGemValue } from "../utils/getGemValue";

interface ProcessingResult {
  success: boolean;
  message: string;
  parsedData?: Record<string, string>[];
  error?: Error;
}

interface ProcessingOptions {
  actionProfile?: string;
  take?: string;
  max?: string;
  provider?: string;
  model?: string;
}

export class ProcessingService {
  private static instance: ProcessingService;

  private constructor() {}

  public static getInstance(): ProcessingService {
    if (!ProcessingService.instance) {
      ProcessingService.instance = new ProcessingService();
    }
    return ProcessingService.instance;
  }

  /**
   * Process a PDF file and store the results
   * @param file The PDF file to process
   * @param fileItemId Optional file ID if the file is already stored in the database
   * @param options Processing options
   * @returns Processing result
   */
  public async processPdf(
    file: File,
    fileItemId?: string,
    options: ProcessingOptions = {},
  ): Promise<ProcessingResult> {
    try {
      // If no fileItemId is provided, save the file to get one
      if (!fileItemId) {
        fileItemId = await filesService.saveFile(file);
        console.log("File saved to IndexedDB with ID:", fileItemId);
      }

      // Prepare form data with options
      const formData = new FormData();
      formData.append(
        "actionProfile",
        options.actionProfile || "firstTakeTransactionsAlt",
      );
      formData.append("inputFile", file);
      formData.append("Take", options.take || "500");
      formData.append("Max", options.max || "1000");
      formData.append("Provider", options.provider || "a");
      formData.append("Model", options.model || "c37s");

      console.log("Processing file:", file.name);

      // Get configuration and API endpoint
      const config = appConfigService.getConfig();
      const value = getGemValue();
      const baseUrl = config.geminiApiUrl ? config.geminiApiUrl : "https://gemini-api.pdfconvertone.com";
      const transactionsEndpoint = "/execute";
      const url = `${baseUrl}${transactionsEndpoint}`;

      console.log("executing URL:", url);

      // Call the API
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${value}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const apiData = await response.json();
      console.log("API response received");

      if (!apiData.responseJson) {
        return {
          success: false,
          message: "Response does not contain responseJson field",
        };
      }

      try {
        // Parse the response data
        const result = await textToJsonParserService.parseToJson(
          apiData.responseJson,
        );

        const parsedData = result.records;
        if (!Array.isArray(parsedData)) {
          return {
            success: false,
            message: "Parsed responseJson is not an array",
          };
        }

        // Update the stored file with parsed results
        await filesService.updateFileResults(
          fileItemId,
          apiData.responseJson,
          JSON.stringify(parsedData),
        );

        return {
          success: true,
          message: "File processed successfully",
          parsedData,
        };
      } catch (parseError) {
        console.error("Error parsing responseJson:", parseError);
        return {
          success: false,
          message: "Error parsing response data",
          error: parseError instanceof Error
            ? parseError
            : new Error(String(parseError)),
        };
      }
    } catch (error) {
      console.error("Error processing PDF:", error);
      return {
        success: false,
        message: "Error processing PDF file",
        error: error instanceof Error ? error : new Error(String(error)),
      };
    }
  }

  /**
   * Reprocess an existing file using its ID
   * @param fileItemId The ID of the file to reprocess
   * @param options Processing options
   * @returns Processing result
   */
  public async reprocessFile(
    fileItemId: string,
    options: ProcessingOptions = {},
  ): Promise<ProcessingResult> {
    try {
      // Get the file from the database
      const fileItem = await filesService.getFile(fileItemId);
      if (!fileItem) {
        return {
          success: false,
          message: "File not found",
        };
      }

      // Process the file with the original PDF content
      return await this.processPdf(fileItem.pdfRawContent, fileItemId, options);
    } catch (error) {
      console.error("Error reprocessing file:", error);
      return {
        success: false,
        message: "Error reprocessing file",
        error: error instanceof Error ? error : new Error(String(error)),
      };
    }
  }
}

export const processingService = ProcessingService.getInstance();
